import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import { ColorResult } from "react-color";

import { ElementState, Preview } from "@creatomate/preview";
import { SelectOption } from "@culturehq/components/dist/components/form/typings";

import { BuiltInFontDefinition } from "components/stories/video-studio/fonts/BuiltInFonts";
import FontFamily from "components/stories/video-studio/common/properties/text/FontFamily";
import FillColor from "components/stories/video-studio/common/properties/FillColor";
import FontSize from "components/stories/video-studio/common/properties/text/FontSize";
import FontStyle from "components/stories/video-studio/common/properties/text/FontStyle";
import FontWeight from "components/stories/video-studio/common/properties/text/FontWeight";
import { getRgbColor } from "components/stories/video-studio/utils";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import isSubtitleText from "../utils/isSubtitleText";
import modificationsForAllSubtitles from "../utils/modificationsForAllSubtitles";

interface SubtitlesFontValuesProps {
  activeElement: ElementState;
  onSetSelectedFont: Dispatch<SetStateAction<BuiltInFontDefinition | undefined>>;
  parsedFonts: BuiltInFontDefinition[];
  preview: Preview;
  selectedFont?: BuiltInFontDefinition;
}

const SubtitlesFontValues: React.FC<SubtitlesFontValuesProps> = ({
  activeElement,
  onSetSelectedFont,
  parsedFonts,
  preview,
  selectedFont
}) => {
  const { data: { burnSubtitles } } = useContext(CreatomateContext);

  const [styles, setStyles] = useState<SelectOption[]>([]);
  const [weights, setWeights] = useState<SelectOption[]>([]);

  const elements = preview.state?.elements || [];
  const subtitleElements = useMemo(
    () => elements.filter(element => isSubtitleText(element.source.name)), [elements]
  );

  useEffect(
    () => {
      const getFontWeights = () => {
        const newFontValue = parsedFonts.find(
          parsedFont => parsedFont.family === activeElement.source.font_family
        );
        setWeights(newFontValue?.weights?.map(weight => ({ label: `${weight}`, value: `${weight}` })) || []);
      };

      const getFontStyles = () => {
        const newFontValue = parsedFonts.find(
          parsedFont => parsedFont.family === activeElement.source.font_family
        );
        setStyles(newFontValue?.styles?.map(style => ({ label: `${style.charAt(0).toUpperCase()}${style.slice(1)}`, value: style })) || []);
      };

      if (activeElement?.source?.type === "text" && isSubtitleText(activeElement?.source?.name)
        && activeElement?.source?.font_family) {
        getFontWeights();
        getFontStyles();
      }
    }, [activeElement, parsedFonts]
  );

  const handleFontChange = (value: BuiltInFontDefinition) => {
    onSetSelectedFont(value);
    setWeights(value.weights.map(weight => ({ label: `${weight}`, value: `${weight}` })) || []);
    setStyles(value.styles.map(style => ({ label: `${style.charAt(0).toUpperCase()}${style.slice(1)}`, value: style })) || []);
  };

  const handleFontPropertyChange = async (propertyName: string, value: string) => {
    let modifications = {};
    if (propertyName === "font_family") {
      const newFontValue = parsedFonts.find(
        parsedFont => parsedFont.family === value
      ) || selectedFont || parsedFonts[0];
      modifications = {
        ...modificationsForAllSubtitles(subtitleElements, propertyName, value),
        ...modificationsForAllSubtitles(subtitleElements, "font_weight", newFontValue.weights.find(font => font === 400) ? "400" : newFontValue.weights[0]),
        ...modificationsForAllSubtitles(subtitleElements, "font_style", newFontValue.styles.find(font => font === "normal") ? "normal" : newFontValue.styles[0])
      };
    } else {
      modifications = modificationsForAllSubtitles(subtitleElements, propertyName, value);
    }
    await preview.applyModifications(modifications);
  };

  const handleColorChange = async (color: ColorResult) => {
    await preview.applyModifications(modificationsForAllSubtitles(
      subtitleElements, "fill_color", getRgbColor(color)
    ));
  };

  const handleFontSizeChange = async (value: string) => {
    const fontSize = +value;
    await preview.applyModifications(modificationsForAllSubtitles(
      subtitleElements, "font_size", fontSize || null
    ));
  };

  if (parsedFonts.length === 0) return <></>;

  return (
    <>
      <div className="FontOptions">
        <FontFamily
          activeElement={activeElement}
          defaultValue={selectedFont?.family ? selectedFont.family : parsedFonts[0].family}
          disabled={!burnSubtitles}
          onFontChange={handleFontChange}
          onSubtitleChange={handleFontPropertyChange}
          parsedFonts={parsedFonts}
          preview={preview}
        />
        <FillColor
          activeElement={activeElement}
          disabled={!burnSubtitles}
          onSubtitleChange={handleColorChange}
          preview={preview}
        />
      </div>
      <div className="FontOptions-Secondary">
        <FontSize
          activeElement={activeElement}
          onSubtitleChange={handleFontSizeChange}
          preview={preview}
        />
        <FontWeight
          activeElement={activeElement}
          onSubtitleChange={handleFontPropertyChange}
          preview={preview}
          selectedFont={selectedFont}
          weights={weights}
        />
        <FontStyle
          activeElement={activeElement}
          onSubtitleChange={handleFontPropertyChange}
          preview={preview}
          selectedFont={selectedFont}
          styles={styles}
        />
      </div>
    </>
  );
};

export default SubtitlesFontValues;
